import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Users, Leaf, Star, Shield, Moon, Sun, Gem, Eye, Zap, Globe, ExternalLink } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import CrystalMusicPlayer from '../components/CrystalMusicPlayer';

const ZEN_IMAGE = 'https://images.unsplash.com/photo-1550007436-ba84ff7bcb54?auto=format&fit=crop&w=800&q=85';
const CRYSTAL_ARRANGEMENT = 'https://images.pexels.com/photos/6757423/pexels-photo-6757423.jpeg?auto=compress&cs=tinysrgb&w=800';

const AboutPage = () => {
  usePageTitle('About Us');
  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <div className="relative py-24 px-6 bg-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, #9b7bb8 0%, transparent 50%),
                             radial-gradient(circle at 80% 50%, #c9a96e 0%, transparent 50%)`
          }} />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="w-4 h-4 text-[#c9a96e]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-medium">Our Story</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white mb-6">
            Awakening Your Divine Light
          </h1>
          <p className="text-lg text-[#bbb5ae] max-w-2xl mx-auto">
            Where ancient wisdom meets modern healing. Discover the transformative power of crystals
            and unlock the magnificent being you were always meant to be.
          </p>
        </div>
      </div>

      {/* Crystal Music Player — THE SHOWSTOPPER */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#1e1919] via-[#0a0a20] to-[#faf8f5] relative overflow-hidden" data-testid="music-player-section">
        {/* Ambient background particles */}
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-0.5 h-0.5 bg-white rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 80}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.15}s`,
              opacity: 0.15 + Math.random() * 0.35,
            }}
          />
        ))}
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-4">
              <Gem className="w-4 h-4 text-[#c9a96e]" />
              <span className="text-sm text-white/80 tracking-widest uppercase">Now Playing</span>
            </div>
            <p className="text-white/40 text-sm max-w-md mx-auto">
              Press the crystal to feel the vibe. Let the frequencies align your energy.
            </p>
          </div>
          <CrystalMusicPlayer />
        </div>
      </section>

      {/* Main About Content */}
      <div className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Opening Statement */}
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1e1919] mb-6">
              Welcome to Your Sacred Journey of Transformation
            </h2>
            <p className="text-lg text-[#61525a] leading-relaxed">
              At Gemstone Minerals, we believe that within every soul lies an infinite well of power,
              wisdom, and radiant light waiting to be awakened. You are not here by accident.
              The universe has guided you to this moment because you are ready — ready to embrace
              your highest potential and step into the magnificent life you deserve.
            </p>
          </div>

          {/* Story Section with Zen Wellness Image */}
          <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#1e1919] mb-6">
                The Magic of Crystal Healing & Metaphysical Wellness
              </h3>
              <div className="space-y-6 text-[#61525a] leading-relaxed">
                <p>
                  For millennia, ancient civilizations across the globe have recognized the extraordinary
                  <strong className="text-[#9D4EDD]"> healing properties of gemstones</strong> and crystals.
                  From the mystical temples of Egypt to the sacred mountains of Tibet, from the
                  <strong className="text-[#9D4EDD]"> shamanic traditions</strong> of the Americas to the
                  Ayurvedic wisdom of India, crystals have been revered as powerful conduits of
                  <strong className="text-[#9D4EDD]"> divine energy and spiritual transformation</strong>.
                </p>
                <p>
                  Today, the science of <strong className="text-[#9D4EDD]">vibrational healing</strong> and
                  <strong className="text-[#9D4EDD]"> crystal therapy</strong> is experiencing a beautiful
                  renaissance. More people than ever are discovering what the ancients always knew:
                  that these precious gifts from Mother Earth carry frequencies that can align our
                  <strong className="text-[#9D4EDD]"> chakras</strong>, clear energetic blockages, and
                  restore balance to our mind, body, and spirit.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={ZEN_IMAGE}
                  alt="Healing crystals in serene wellness arrangement"
                  className="w-full h-full object-cover"
                  data-testid="zen-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 glass-effect px-6 py-4 rounded-xl shadow-lg bg-white/90">
                <p className="text-3xl font-serif text-[#c9a96e]">22+</p>
                <p className="text-xs uppercase tracking-wider text-[#61525a]">Years of Expertise</p>
              </div>
            </div>
          </div>

          {/* Empowering Message Section */}
          <div className="bg-gradient-to-br from-[#9D4EDD]/5 via-[#faf8f5] to-[#FF6B9D]/5 rounded-3xl p-10 md:p-16 mb-20">
            <div className="text-center mb-10">
              <Star className="w-10 h-10 text-[#FFB800] mx-auto mb-4" />
              <h3 className="font-serif text-2xl md:text-3xl text-[#1e1919] mb-4">
                You Are Worthy of Abundance, Love, and Healing
              </h3>
            </div>
            <div className="space-y-6 text-[#61525a] leading-relaxed text-lg">
              <p>
                We want you to know something important, something that perhaps you've forgotten
                in the chaos of everyday life: <strong className="text-[#9D4EDD]">You are extraordinary</strong>.
                You are a being of limitless potential, capable of creating miracles in your own life
                and in the lives of others. The challenges you've faced haven't diminished your light — they've
                refined it, strengthened it, made it shine even brighter.
              </p>
              <p>
                Perhaps you've been searching for something — a sense of peace, a path to
                <strong className="text-[#9D4EDD]"> spiritual awakening</strong>, a way to heal old wounds,
                or simply a reminder that <strong className="text-[#9D4EDD]">magic is real</strong>.
                We're here to tell you that your search has led you to the right place.
              </p>
            </div>
          </div>

          {/* Chakra & Energy Section */}
          <div className="mb-20">
            <h3 className="font-serif text-2xl md:text-3xl text-[#1e1919] mb-8 text-center">
              The Seven Chakras: Your Energy Centers of Transformation
            </h3>
            <p className="text-[#61525a] leading-relaxed text-lg mb-10 text-center max-w-3xl mx-auto">
              Your body is a magnificent temple of energy, with seven major
              <strong className="text-[#9D4EDD]"> chakra energy centers</strong> that govern different
              aspects of your physical, emotional, and spiritual well-being.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { name: 'Root Chakra', color: '#FF6B6B', desc: 'Grounding, security, survival', crystal: 'Black Tourmaline' },
                { name: 'Sacral Chakra', color: '#FF9F43', desc: 'Creativity, passion, pleasure', crystal: 'Carnelian' },
                { name: 'Solar Plexus', color: '#FFB800', desc: 'Power, confidence, will', crystal: 'Citrine' },
                { name: 'Heart Chakra', color: '#00C853', desc: 'Love, compassion, healing', crystal: 'Rose Quartz' },
                { name: 'Throat Chakra', color: '#00B4D8', desc: 'Communication, truth, expression', crystal: 'Blue Lace Agate' },
                { name: 'Third Eye', color: '#7B68EE', desc: 'Intuition, wisdom, insight', crystal: 'Amethyst' },
                { name: 'Crown Chakra', color: '#9D4EDD', desc: 'Spirituality, enlightenment', crystal: 'Selenite' },
                { name: 'Your Aura', color: '#C77DFF', desc: 'Protection, radiance, magnetism', crystal: 'Clear Quartz' }
              ].map((chakra, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
                  <div className="w-10 h-10 rounded-full mb-4" style={{ backgroundColor: chakra.color }} />
                  <h4 className="font-semibold text-[#1e1919] mb-2">{chakra.name}</h4>
                  <p className="text-sm text-[#7A7A9A] mb-2">{chakra.desc}</p>
                  <p className="text-xs text-[#9D4EDD] font-medium">Crystal: {chakra.crystal}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mission Section with Crystal Arrangement */}
          <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
            <div className="relative order-2 md:order-1">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img src={CRYSTAL_ARRANGEMENT} alt="Stunning amethyst crystal geode with deep purple hues" className="w-full h-full object-cover" data-testid="crystal-arrangement-image" />
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h3 className="font-serif text-2xl md:text-3xl text-[#1e1919] mb-6">
                Our Sacred Mission: Empowering Your Spiritual Journey
              </h3>
              <div className="space-y-6 text-[#61525a] leading-relaxed">
                <p>
                  At Gemstone Minerals, we are more than a <strong className="text-[#9D4EDD]">crystal shop</strong> — we
                  are guardians of ancient wisdom, curators of Earth's most precious treasures, and
                  devoted believers in your unlimited potential. Every crystal we offer has been
                  <strong className="text-[#9D4EDD]"> hand-selected</strong> for its energetic purity and
                  <strong className="text-[#9D4EDD]"> healing vibration</strong>.
                </p>
                <p>
                  From the deep purple majesty of <strong className="text-[#9D4EDD]">Amethyst</strong> to the
                  loving pink embrace of <strong className="text-[#9D4EDD]">Rose Quartz</strong>, from the
                  protective power of <strong className="text-[#9D4EDD]">Black Tourmaline</strong> to the
                  celestial clarity of <strong className="text-[#9D4EDD]">Selenite</strong> — each stone
                  carries a unique frequency designed to support your journey.
                </p>
              </div>
            </div>
          </div>

          {/* Manifestation & Magic Section */}
          <div className="bg-[#1e1919] rounded-3xl p-10 md:p-16 mb-20 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 30% 30%, #9D4EDD 0%, transparent 40%),
                                 radial-gradient(circle at 70% 70%, #FFB800 0%, transparent 40%)`
              }} />
            </div>
            <div className="relative z-10">
              <div className="text-center mb-10">
                <Moon className="w-10 h-10 text-[#c9a96e] mx-auto mb-4" />
                <h3 className="font-serif text-2xl md:text-4xl text-white mb-4" style={{ textShadow: '0 0 30px rgba(201,169,110,0.4)' }}>
                  Unlock the Magic Within: <span className="text-[#c9a96e]">Manifestation &amp; Crystal Energy</span>
                </h3>
              </div>
              <div className="space-y-6 text-[#bbb5ae] leading-relaxed text-lg max-w-3xl mx-auto">
                <p>
                  There is a profound <strong className="text-[#c9a96e]">magic</strong> that happens when
                  you align your intentions with the right crystal. Everything in the
                  universe, including you and every crystal on Earth, vibrates at a specific frequency.
                  When you bring a crystal into your energy field, its frequency begins to harmonize
                  with yours, gently shifting your vibration toward healing, abundance, and alignment.
                </p>
                <p>
                  <strong className="text-[#c9a96e]">Manifestation</strong> becomes easier when you have
                  the right energetic support. Whether you're calling in
                  <strong className="text-[#c9a96e]"> financial abundance</strong>,
                  <strong className="text-[#c9a96e]"> soul-deep love</strong>,
                  <strong className="text-[#c9a96e]"> career success</strong>, or
                  <strong className="text-[#c9a96e]"> physical healing</strong>, there's a crystal
                  that resonates with that specific intention.
                </p>
              </div>
            </div>
          </div>

          {/* Self-Worth Section */}
          <div className="text-center mb-20">
            <Sun className="w-10 h-10 text-[#FFB800] mx-auto mb-6" />
            <h3 className="font-serif text-2xl md:text-3xl text-[#1e1919] mb-8">
              Remember Who You Truly Are
            </h3>
            <div className="max-w-3xl mx-auto space-y-6 text-lg text-[#61525a] leading-relaxed">
              <p>
                In this fast-paced world, it's easy to forget your own divinity. <strong className="text-[#9D4EDD]">We're here
                to remind you that you are extraordinary.</strong> You are a being of limitless potential.
                The same force that moves the planets, grows the trees, and creates diamonds in the Earth's depths
                flows through you. <strong className="text-[#9D4EDD]">You are that powerful.</strong>
              </p>
              <p>
                Our crystals are here to remind you of this truth. When you hold an
                <strong className="text-[#9D4EDD]"> Amethyst</strong> and feel its calming energy, you're
                not borrowing peace from something external — you're awakening the peace that already
                lives within you.
              </p>
            </div>
          </div>

          {/* Services Highlight */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {[
              { icon: Gem, title: 'Premium Crystals', description: 'Hand-selected healing gemstones ethically sourced from around the world, each carrying powerful vibrational energy.' },
              { icon: Eye, title: 'Spiritual Guidance', description: 'Tarot readings, psychic consultations, and personalized crystal prescriptions to support your unique journey.' },
              { icon: Zap, title: 'Transformational Education', description: 'Live webinars, workshops, and membership programs to deepen your metaphysical knowledge and practice.' }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all group text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-serif text-xl text-[#1e1919] mb-4">{service.title}</h4>
                <p className="text-[#61525a]">{service.description}</p>
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="text-center bg-gradient-to-br from-[#F8F5FF] to-[#FFF5F8] rounded-3xl p-10 md:p-16">
            <Globe className="w-12 h-12 text-[#9D4EDD] mx-auto mb-6" />
            <h3 className="font-serif text-2xl md:text-3xl text-[#1e1919] mb-6">
              Your Transformation Begins Now
            </h3>
            <p className="text-lg text-[#61525a] leading-relaxed max-w-2xl mx-auto mb-10">
              This is your invitation to step into a life of greater purpose, deeper healing, and
              unlimited abundance. <strong className="text-[#9D4EDD]">You are worthy of every blessing headed your way.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/collection" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-[0_8px_25px_rgba(157,78,221,0.4)] transition-all duration-300 hover:scale-105">
                <Gem className="w-5 h-5" />
                Explore Sacred Six Collection
              </Link>
              <Link to="/education" className="inline-flex items-center justify-center gap-2 border-2 border-[#9D4EDD] text-[#9D4EDD] px-8 py-4 rounded-xl font-semibold hover:bg-[#9D4EDD] hover:text-white transition-all duration-300">
                <Star className="w-5 h-5" />
                Join Our Community
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-medium mb-4 block">What We Stand For</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1e1919]">Our Core Values</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Heart, title: 'Authenticity', description: 'Every crystal is genuine and naturally formed. We never sell synthetic or treated stones without full disclosure.' },
              { icon: Leaf, title: 'Ethical Sourcing', description: 'We partner with miners and suppliers who prioritize environmental responsibility and sustainable extraction methods.' },
              { icon: Users, title: 'Community', description: 'We believe in building a supportive global community of crystal lovers, healers, lightworkers, and spiritual seekers.' }
            ].map((value, index) => (
              <div key={index} className="text-center p-8 rounded-2xl bg-[#faf8f5] hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#c9a96e]/10 flex items-center justify-center">
                  <value.icon className="w-7 h-7 text-[#c9a96e]" />
                </div>
                <h3 className="font-serif text-xl text-[#1e1919] mb-4">{value.title}</h3>
                <p className="text-[#61525a]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Kholey Ownership Section */}
      <div className="py-16 px-6 bg-gradient-to-r from-[#0a0a20] to-[#1e1919]" data-testid="kholey-section">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-medium mb-3">A Kholey Venture</p>
              <h3 className="font-serif text-2xl text-white mb-4">
                Powered by <span className="text-[#c9a96e]">Kholey</span>
              </h3>
              <p className="text-white/60 leading-relaxed mb-6">
                Gemstone Minerals is a proud venture of <strong className="text-[#c9a96e]">Kholey</strong> — a creative
                force behind brands that blend innovation, spiritual depth, and purpose-driven commerce.
                Where Kholey brings the vision and business infrastructure, Gemstone Minerals channels
                that energy into the world of healing crystals, metaphysical wellness, and sacred transformation.
              </p>
              <p className="text-white/40 text-sm mb-6">
                Together, these brands share a unified mission: to empower, heal, and elevate through authenticity,
                intentional design, and a deep respect for Earth's wisdom.
              </p>
              <a
                href="https://kholey.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-[#c9a96e]/30 rounded-xl text-[#c9a96e] font-semibold hover:bg-[#c9a96e]/20 transition-all"
                data-testid="kholey-link"
              >
                Visit Kholey.com <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <div className="flex-shrink-0">
              <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-[#c9a96e] to-[#FFB800] flex items-center justify-center shadow-xl shadow-[#c9a96e]/20">
                <span className="font-serif text-4xl text-[#1e1919] font-bold">K</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="py-20 px-6 bg-gradient-to-br from-[#9b7bb8]/10 via-[#faf8f5] to-[#c9a96e]/10">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-10 h-10 text-[#9D4EDD] mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl text-[#1e1919] mb-6">We're Here for You</h2>
          <p className="text-[#61525a] mb-8 max-w-xl mx-auto text-lg">
            Have questions about which crystals are right for you? Need guidance on your
            spiritual path? We're here to help you every step of the way.
          </p>
          <a href="tel:+14244507018" className="inline-flex items-center gap-2 bg-[#1e1919] text-white px-8 py-4 rounded-xl hover:bg-[#c9a96e] transition-colors font-medium tracking-wider text-sm uppercase">
            Call Us: +1 (424) 450-7018
          </a>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
